import { phone as parsePhone } from "phone";

import { countryCodes } from "./country-codes.ts";

/** Dial-code lookup by ISO country ("US" → "+1"). */
const dialCodeByIso: Record<string, string> = {};
for (const entry of countryCodes) {
  if (!dialCodeByIso[entry.country]) dialCodeByIso[entry.country] = entry.code;
}

type PhoneParts = { value?: unknown; countryCode?: unknown; country?: unknown };

/** Resolve a phone value — an E.164/free-typed string, or the stored
 *  `{ value, countryCode, country }` pair (whose `countryCode` is a dial code
 *  from the form half or an ISO country from the stored half) — to one
 *  dialable string. Null when the value holds no number. */
function toDialString(value: unknown): string | null {
  if (typeof value === "string") {
    const text = value.trim();
    return text || null;
  }
  if (typeof value === "object" && value !== null) {
    const parts = value as PhoneParts;
    const national = parts.value == null ? "" : String(parts.value).trim();
    if (!national) return null;
    if (national.startsWith("+")) return national;
    const key = String(parts.country ?? parts.countryCode ?? "");
    const dial = key.startsWith("+") ? key : dialCodeByIso[key];
    return `${dial ?? ""}${national}`;
  }
  return null;
}

/** Known dial codes, longest first, so a prefix match is never eclipsed by a
 *  shorter code ("+358" wins over "+35"-alikes). */
const dialCodesByLength = [...new Set(countryCodes.map((c) => c.code))].sort(
  (a, b) => b.length - a.length,
);

/** First ISO country per dial code — "+1" reads as US, the table's order. */
const isoByDialCode: Record<string, string> = {};
for (const entry of countryCodes) {
  if (!isoByDialCode[entry.code]) isoByDialCode[entry.code] = entry.country;
}

/** Split a raw "+<code><national>" string on the longest KNOWN dial code.
 *  This is how an UNPARSEABLE number keeps its country: a pair whose digits do
 *  not fit the selected region persists as the raw concatenation (see
 *  `fieldDto`), and the prefix is the only country information it carries.
 *  Null when the text does not start with a known code. */
export function splitDialCode(
  text: string,
): { code: string; national: string; country: string } | null {
  if (!text.startsWith("+")) return null;
  for (const code of dialCodesByLength) {
    if (text.startsWith(code) && text.length > code.length) {
      return {
        code,
        national: text.slice(code.length),
        country: isoByDialCode[code] ?? "US",
      };
    }
  }
  return null;
}

/** Whether a phone value (stored string or `{value, countryCode}` pair) holds
 *  a number that parses for its region. Empty is valid — it is unanswered.
 *  The commit gate and the API refuse a value it rejects; the display marker
 *  still reads it for numbers saved before that rule. */
export function isValidPhoneValue(value: unknown): boolean {
  const raw = toDialString(value);
  if (!raw) return true;
  return parsePhone(raw, { validateMobilePrefix: false }).isValid;
}

/** NANP has exactly one national presentation, so it needs no metadata. */
const formatNanp = (digits: string) =>
  `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;

/** Display formatting for any phone value this package's DTO produces or a
 *  free-text phone property holds.
 *
 *  NANP (+1) numbers read in their national format, `(208) 555-0123`; every
 *  other region reads as dial code + national digits, `+44 7911123456` —
 *  region-correct grouping for 200 countries is exactly the libphonenumber
 *  metadata this package deliberately does not carry (see AGENTS.md), and the
 *  dial-code split is unambiguous and dialable without it. A bare national
 *  number parses against the parser's US default, matching validation, and a
 *  value that does not parse travels through as typed rather than being
 *  reshaped. */
export function formatPhoneNumber(value: unknown): string {
  const raw = toDialString(value);
  if (!raw) return "";
  const parsed = parsePhone(raw, { validateMobilePrefix: false });
  if (!parsed.isValid) {
    // An unparseable value still reads dial code + digits when a known code
    // prefixes it — "+3762093922222" is a wrong-length Andorra number, and
    // "+376 2093922222" keeps the country legible without pretending the
    // number is well-formed. Anything else travels as typed.
    const split = splitDialCode(raw);
    return split ? `${split.code} ${split.national}` : raw;
  }
  const national = parsed.phoneNumber.slice(parsed.countryCode.length);
  return parsed.countryCode === "+1"
    ? formatNanp(national)
    : `${parsed.countryCode} ${national}`;
}

/** Normalize free-typed phone text to E.164 for storage — the same
 *  normalization `fieldDto` applies to the phone custom field, for the
 *  free-text phone properties (contact/account phone, a user's profile
 *  number). Text that does not parse persists as typed: a CRM must never
 *  drop a number a rep wrote down. */
export function normalizePhoneNumber(raw: string): string {
  const text = raw.trim();
  if (!text) return text;
  const parsed = parsePhone(text, { validateMobilePrefix: false });
  return parsed.isValid ? parsed.phoneNumber : text;
}

/** Progressive NANP grouping for a number BEING TYPED — each group closes as
 *  soon as it completes ("213" → "(213)", the hyphen lands with the 7th
 *  digit), so the editor can format as the user types. Takes national DIGITS
 *  only. Digits past the 10 NANP places ride along unformatted: a too-long
 *  draft stays legible (and invalid) rather than reshaped. */
export function formatNanpAsYouType(digits: string): string {
  if (digits.length < 3) return digits;
  if (digits.length === 3) return `(${digits})`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}${digits.slice(10)}`;
}
